import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart, getCalendarUnitPrice } from '../context/CartContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SocialShareButtons from '../components/SocialShareButtons';
import { SHOP_DROPDOWN, CALENDAR_DROPDOWN, CALENDAR_BW_PATH } from '../components/navConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Highlights whichever calendar this page is showing in both dropdowns.
const navItems = (slug) => {
  const active = slug && CALENDAR_BW_PATH === `/shop/calendar/${slug}` ? 'calendar-bw' : 'calendar';
  return [
    { to: '/', label: 'Home' },
    { label: 'Shop', dropdown: SHOP_DROPDOWN(active) },
    CALENDAR_DROPDOWN(active),
    { to: '/archive', label: 'Archive' },
    { to: '/about', label: 'About' },
  ];
};

const FOOTER_LINKS = [
  { to: '/about', label: 'About' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/admin/login', label: 'Admin' },
];

function arrowBtnStyle(side) {
  return {
    position: 'absolute', [side]: '10px', top: '50%', transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%',
    width: '40px', height: '40px', cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
  };
}

function thumbBtnStyle(active) {
  return {
    aspectRatio: '1', padding: 0, border: active ? '2px solid #7c3aed' : '2px solid transparent',
    borderRadius: '4px', overflow: 'hidden', cursor: 'pointer', background: 'none'
  };
}

const selectStyle = {
  padding: '10px 15px', border: '1px solid #ccc', borderRadius: '4px',
  fontSize: '14px', background: '#fff', cursor: 'pointer'
};

function Calendar() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [otherCalendars, setOtherCalendars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const { addItem, itemCount, openCart } = useCart();

  useEffect(() => {
    setQuantity(1);
    setSelectedImageIndex(0);
    fetchProduct();
  }, [slug]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/calendar-products`);
      // /shop/calendar shows the first calendar; any other one lives at
      // /shop/calendar/{its slug}.
      const all = response.data;
      const current = (slug ? all.find(p => p.slug === slug) : all[0]) || null;
      setProduct(current);
      setOtherCalendars(all
        .filter(p => p.id !== current?.id)
        .map(p => ({ ...p, href: p.slug ? `/shop/calendar/${p.slug}` : (p.id === all[0].id ? '/shop/calendar' : null) }))
        .filter(p => p.href));
    } catch (error) {
      console.error('Failed to fetch calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="substack-site">
        <Header navItems={navItems(slug)} cart={{ itemCount, onClick: openCart }} />
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner" />
        </div>
        <Footer links={FOOTER_LINKS} />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="substack-site">
        <Header navItems={navItems(slug)} cart={{ itemCount, onClick: openCart }} />
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <h2>Calendar Coming Soon</h2>
          <p style={{ color: '#666' }}>Check back soon for this year's calendar!</p>
        </div>
        <Footer links={FOOTER_LINKS} />
      </div>
    );
  }

  const outOfStock = product.stock === 0;
  const maxQty = product.stock == null ? 20 : Math.max(1, Math.min(20, product.stock));
  const qtyOptions = Array.from({ length: maxQty }, (_, i) => i + 1);
  const allImages = [product.image_url, ...(product.additional_images || [])].filter(Boolean);
  const unitPrice = getCalendarUnitPrice(quantity);

  const handleAddToCart = () => {
    addItem({
      shop: 'calendar',
      product_id: product.id,
      product_title: product.title,
      image_url: product.image_url,
      variant: null,
      variant_label: null,
      price: product.price,
      quantity,
      max_quantity: product.stock,
      special_instructions: null
    });
    toast.success(`${product.title} added to cart!`);
  };

  const nextImage = () => setSelectedImageIndex((prev) => (prev + 1) % allImages.length);
  const prevImage = () => setSelectedImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);

  return (
    <div className="substack-site">
      <Header navItems={navItems(slug)} cart={{ itemCount, onClick: openCart }} />

      <main style={{ maxWidth: '1100px', margin: '0 auto', padding: '30px 20px 60px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '50px', alignItems: 'start' }}>
          {/* Left Column - Image Gallery */}
          <div>
            <div style={{ position: 'relative', marginBottom: '15px' }}>
              <img
                src={allImages[selectedImageIndex]}
                alt={product.title}
                style={{ width: '100%', height: 'auto', borderRadius: '4px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}
              />
              {allImages.length > 1 && (
                <>
                  <button onClick={prevImage} style={arrowBtnStyle('left')}><ChevronLeft size={24} /></button>
                  <button onClick={nextImage} style={arrowBtnStyle('right')}><ChevronRight size={24} /></button>
                </>
              )}
            </div>

            {allImages.length > 1 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                {allImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImageIndex(idx)}
                    style={thumbBtnStyle(selectedImageIndex === idx)}
                  >
                    <img src={img} alt={`${product.title} ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Product Info */}
          <div>
            <h1 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: '32px',
              fontWeight: '400',
              margin: '0 0 12px',
              color: '#1a1a1a'
            }}>
              {product.title}
            </h1>

            {product.tagline && (
              <p style={{ fontSize: '15px', color: '#666', margin: '0 0 20px', fontStyle: 'italic', lineHeight: '1.6' }}>
                {product.tagline}
              </p>
            )}

            <div style={{ fontSize: '28px', fontWeight: '500', color: '#1a1a1a', marginBottom: '4px' }}>
              ${unitPrice.toFixed(2)}{quantity > 1 ? ' each' : ''}
            </div>

            <div style={{
              display: 'flex', gap: '16px', flexWrap: 'wrap',
              fontSize: '13px', color: '#666', marginBottom: '20px'
            }}>
              <span style={quantity === 1 ? { color: '#7c3aed', fontWeight: '600' } : undefined}>1: $28</span>
              <span style={quantity === 2 ? { color: '#7c3aed', fontWeight: '600' } : undefined}>2: $25 ea</span>
              <span style={quantity >= 3 ? { color: '#7c3aed', fontWeight: '600' } : undefined}>3+: $22 ea + free shipping</span>
              {otherCalendars.length > 0 && <span>Mix &amp; match calendars</span>}
            </div>

            {product.pre_sale && !outOfStock && (
              <div style={{
                display: 'inline-block', background: '#fef3c7', color: '#92400e',
                padding: '6px 14px', borderRadius: '20px', fontSize: '13px',
                fontWeight: '600', marginBottom: '20px'
              }}>
                Pre-Order{product.ships_by ? ` — Ships by ${product.ships_by}` : ''}
              </div>
            )}

            {/* Quantity & Add to Cart */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <label style={{ fontSize: '14px', color: '#666' }}>Qty:</label>
                <select value={quantity} onChange={(e) => setQuantity(parseInt(e.target.value))} style={selectStyle}>
                  {qtyOptions.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>

              {!outOfStock && product.stock != null && product.stock > 0 && product.stock <= 5 && (
                <span style={{ fontSize: '13px', color: '#b45309' }}>Only {product.stock} left</span>
              )}

              {outOfStock ? (
                <div style={{ padding: '12px 30px', background: '#f3f4f6', color: '#9ca3af', borderRadius: '6px', fontSize: '15px', fontWeight: '500' }}>
                  Out of Stock
                </div>
              ) : (
                <Button onClick={handleAddToCart} style={{ padding: '12px 30px', fontSize: '15px', fontWeight: '500' }}>
                  {product.pre_sale ? 'Pre-Order Now' : 'Add to Cart'}
                </Button>
              )}
            </div>

            <div style={{ marginBottom: '25px' }}>
              <p style={{ fontSize: '13px', color: '#888', margin: '0 0 10px' }}>
                Know someone who'd love this? Share it:
              </p>
              <SocialShareButtons
                title={product.title}
                text={product.tagline || product.description}
                imageUrl={product.image_url}
              />
            </div>

            {product.description && (
              <div style={{
                background: '#faf8f5', border: '1px solid #eee', borderRadius: '8px',
                padding: '20px', marginBottom: '20px', fontSize: '15px', color: '#444',
                lineHeight: '1.7', whiteSpace: 'pre-wrap'
              }}>
                {product.description}
              </div>
            )}

            {product.product_details && product.product_details.length > 0 && (
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 12px', color: '#1a1a1a' }}>
                  Product Details
                </h3>
                <ul style={{ listStyle: 'disc', paddingLeft: '20px', margin: 0, color: '#555' }}>
                  {product.product_details.map((line, idx) => (
                    <li key={idx} style={{ fontSize: '14px', lineHeight: '1.8', marginBottom: '4px' }}>{line}</li>
                  ))}
                </ul>
              </div>
            )}

            {otherCalendars.length > 0 && (
              <div style={{ marginTop: '30px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 12px', color: '#1a1a1a' }}>
                  Also Available
                </h3>
                {otherCalendars.map(p => (
                  <Link
                    key={p.id}
                    to={p.href}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '15px', textDecoration: 'none',
                      color: 'inherit', border: '1px solid #eee', borderRadius: '8px',
                      padding: '12px', marginBottom: '10px'
                    }}
                  >
                    <img src={p.image_url} alt={p.title} style={{ width: '70px', height: '70px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: '15px', fontWeight: '600', color: '#1a1a1a', marginBottom: '2px' }}>{p.title}</div>
                      {p.tagline && <div style={{ fontSize: '13px', color: '#666', marginBottom: '2px' }}>{p.tagline}</div>}
                      <div style={{ fontSize: '13px', color: '#7c3aed' }}>See this calendar →</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            <p style={{ fontSize: '13px', color: '#888', margin: '30px 0 0', paddingTop: '20px', borderTop: '1px solid #eee' }}>
              Free shipping on orders over $75 • US shipping only
            </p>
          </div>
        </div>
      </main>

      <Footer links={FOOTER_LINKS} />

      <style>{`
        @media (max-width: 768px) {
          main > div {
            grid-template-columns: 1fr !important;
            gap: 30px !important;
          }
        }
      `}</style>
    </div>
  );
}

export default Calendar;
